"use client";

import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const isStandaloneFullScreen = pathname === "/signup";

  // Create particles on mount
  useEffect(() => {
    if (isStandaloneFullScreen) return;
    const container = document.getElementById('auth-particles');
    if (!container || container.children.length > 0) return;
    
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < 15; i++) {
      const particle = document.createElement('div');
      particle.className = 'auth-particle';
      particle.style.cssText = `
        left: ${Math.random() * 100}%;
        animation-duration: ${Math.random() * 15 + 12}s;
        animation-delay: ${Math.random() * 8}s;
        width: ${Math.random() * 3 + 2}px;
        height: ${Math.random() * 3 + 2}px;
      `;
      fragment.appendChild(particle);
    }
    container.appendChild(fragment);

    return () => {
      if (container) container.innerHTML = '';
    };
  }, []);

  // Some pages (e.g. /signup) render their own full-screen experience.
  // Avoid double backgrounds/logos which can "ghost" through glass cards on mobile.
  if (isStandaloneFullScreen) {
    return <>{children}</>;
  }

  return (
    <>
      <style jsx global>{`
        /* Aurora Background */
        .auth-aurora-bg {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          overflow: hidden;
          background: linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 40%, #0f0a1a 70%, #0a0a0f 100%);
        }

        /* Aurora Blobs */
        .auth-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          will-change: transform;
          contain: layout style;
          transform: translate3d(0, 0, 0);
        }

        .auth-blob-1 {
          top: -20%;
          left: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(168, 85, 247, 0.35) 0%, transparent 70%);
          animation: auth-float-1 20s ease-in-out infinite;
        }

        .auth-blob-2 {
          bottom: -30%;
          right: -10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(236, 72, 153, 0.30) 0%, transparent 70%);
          animation: auth-float-2 25s ease-in-out infinite;
        }

        .auth-blob-3 {
          top: 50%;
          right: 20%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.20) 0%, transparent 70%);
          animation: auth-float-3 18s ease-in-out infinite;
        }

        @keyframes auth-float-1 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate3d(30px, 20px, 0) scale(1.1); opacity: 0.8; }
        }

        @keyframes auth-float-2 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate3d(-25px, -20px, 0) scale(1.05); opacity: 0.7; }
        }

        @keyframes auth-float-3 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.4; }
          50% { transform: translate3d(-15px, 20px, 0) scale(1.1); opacity: 0.6; }
        }

        /* Particles */
        .auth-particles {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          pointer-events: none;
          overflow: hidden;
        }

        .auth-particle {
          position: absolute;
          bottom: -10px;
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.8), rgba(236, 72, 153, 0.8));
          border-radius: 50%;
          animation: auth-particle-rise linear infinite;
          box-shadow: 0 0 6px rgba(168, 85, 247, 0.5);
        }

        @keyframes auth-particle-rise {
          0% { 
            transform: translateY(0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 0.7;
            transform: translateY(-10vh) scale(1);
          }
          90% {
            opacity: 0.5;
          }
          100% { 
            transform: translateY(-110vh) scale(0.5);
            opacity: 0;
          }
        }

        /* Glass Card */
        .auth-glass-card {
          background: rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        /* Glow Text */
        .auth-glow-text {
          text-shadow: 0 0 30px rgba(168, 85, 247, 0.5);
        }

        /* Gradient Button */
        .auth-gradient-btn {
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #f97316 100%);
          background-size: 200% 200%;
          animation: auth-gradient-shift 4s ease infinite;
          position: relative;
          overflow: hidden;
        }

        .auth-gradient-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          animation: auth-btn-shine 3s ease-in-out infinite;
        }

        @keyframes auth-gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes auth-btn-shine {
          0% { left: -100%; }
          50%, 100% { left: 100%; }
        }

        /* Input Styles */
        .auth-input {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: white;
          transition: all 0.3s ease;
        }

        .auth-input:focus {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(168, 85, 247, 0.5);
          box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.15);
          outline: none;
        }

        .auth-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        /* Social Button */
        .auth-social-btn {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.15);
          color: white;
          transition: all 0.3s ease;
        }

        .auth-social-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
        }

        @media (prefers-reduced-motion: reduce) {
          .auth-blob,
          .auth-particle,
          .auth-gradient-btn,
          .auth-gradient-btn::before {
            animation: none;
          }
        }
      `}</style>

      {/* Aurora Background */}
      <div className="auth-aurora-bg">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
        <div className="auth-blob auth-blob-3" />
      </div>

      {/* Particles */}
      <div id="auth-particles" className="auth-particles" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-20 bg-black/30 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-purple-500/30 ring-1 ring-purple-500/30 group-hover:ring-purple-400/50 group-hover:shadow-purple-400/40 transition-all duration-300">
              <Image 
                src="/images/logo-purple.svg" 
                alt="SHOWBOLD" 
                width={40} 
                height={40} 
                className="w-full h-full" 
              />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">SHOWBOLD</span>
          </Link>
          <Link 
            href="/signup" 
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition-all shadow-lg shadow-purple-500/25"
          >
            Get Started
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="fixed inset-0 z-10 flex min-h-screen items-center justify-center overflow-auto px-4 pt-24 pb-8 md:pt-20 md:pb-4">
        <div className="w-full max-w-md my-auto">
          {children}
        </div>
      </div>
    </>
  );
}
